import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useLocation } from '@/contexts/LocationContext';
import { trpc } from '@/lib/trpc';
import { PageErrorBoundary } from '@/components/fallbacks';
import { keepPreviousData } from '@tanstack/react-query';
import { usePageTitle } from '@/contexts/PageTitleContext';
import { PageActionBar } from '@/components/layout/PageActionBar';
import { BackToTopPill } from '@/components/BackToTopPill';

const DEFAULT_PAGE_SIZE = 50;

function InventoryReconciliation() {
  const { t } = useTranslation('inventory');
  const { selectedLocation } = useLocation();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [hasRun, setHasRun] = useState(false);

  useEffect(() => {
    // Force a deliberate run per location so we don't spam Shopify on route changes.
    setHasRun(false);
    setPage(1);
  }, [selectedLocation?.id]);

  const input = useMemo(
    () => ({
      locationId: selectedLocation?.id ?? '',
      page,
      pageSize,
      search: search.trim() ? search.trim() : undefined,
    }),
    [page, pageSize, search, selectedLocation?.id]
  );

  const query = trpc.shopifyIntegration.getInventoryReconciliation.useQuery(input, {
    enabled: Boolean(selectedLocation) && hasRun,
    placeholderData: keepPreviousData,
    retry: 1,
  });

  const totalPages = useMemo(() => {
    const total = query.data?.total ?? 0;
    return total > 0 ? Math.ceil(total / pageSize) : 1;
  }, [pageSize, query.data?.total]);

  const canPrev = page > 1;
  const canNext = page < totalPages;

  const subtitle = useMemo(
    () => (
      <>
        Compare PrintRun on-hand vs Shopify on-hand for <code>PAP</code> SKUs at a specific location.
      </>
    ),
    [],
  );
  usePageTitle(t('reconciliation.title'), subtitle);

  return (
    <div className="flex flex-col">
      <div className="w-full py-6 px-4 sm:px-6 lg:px-8 space-y-4">
        <PageActionBar
          ariaLabel="Reconciliation actions"
          left={
            <>
              <Input
                placeholder={t('reconciliation.searchPlaceholder')}
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                className="h-8 w-[280px] text-xs"
              />
              <Input
                type="number"
                min={1}
                max={200}
                value={pageSize}
                onChange={(e) => {
                  const next = Number(e.target.value);
                  setPageSize(Number.isFinite(next) ? Math.max(1, Math.min(200, next)) : DEFAULT_PAGE_SIZE);
                  setPage(1);
                }}
                className="h-8 w-[100px] text-xs tabular-nums"
                title="Page size"
              />
            </>
          }
          right={
            <Button
              variant="outline"
              onClick={() => {
                if (!selectedLocation) return;
                if (!hasRun) {
                  setHasRun(true);
                  return;
                }
                void query.refetch();
              }}
              disabled={!selectedLocation || query.isFetching}
              className="h-8 px-3 text-xs gap-1.5"
              title={!selectedLocation ? 'Select a location first (top right)' : 'Run reconciliation'}
            >
              {query.isFetching ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
              {t('reconciliation.run')}
            </Button>
          }
        />

        <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-muted-foreground">
          <span className="text-xs">
            Location: <span className="font-medium">{selectedLocation?.name ?? 'All Locations (select one in the header)'}</span>
          </span>
          {query.data?.checkedAt ? (
            <span>
              Checked <span className="font-medium">{new Date(query.data.checkedAt).toLocaleString()}</span>
            </span>
          ) : (
            <span>{t('reconciliation.notRunYet')}</span>
          )}
        </div>

        {query.error && (
          <div className="rounded-md border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
            {query.error.message}
          </div>
        )}

        {!selectedLocation && (
          <div className="rounded-md border p-4 text-sm text-muted-foreground">
            {t('reconciliation.selectLocationBanner')}
          </div>
        )}

        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[180px]">{t('reconciliation.skuCol')}</TableHead>
                <TableHead>{t('reconciliation.titleCol')}</TableHead>
                <TableHead className="w-[140px] text-right">{t('reconciliation.erpOnHand')}</TableHead>
                <TableHead className="w-[140px] text-right">{t('reconciliation.shopifyOnHand')}</TableHead>
                <TableHead className="w-[120px] text-right">{t('reconciliation.delta')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {!query.data ? (
                <TableRow>
                  <TableCell colSpan={5} className="h-24 text-center text-muted-foreground">
                    {query.isFetching ? (
                      <span className="inline-flex items-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        {t('reconciliation.loading')}
                      </span>
                    ) : (
                      t('reconciliation.runPrompt')
                    )}
                  </TableCell>
                </TableRow>
              ) : query.data.rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="h-24 text-center text-muted-foreground">
                    {t('reconciliation.noPapSkus')}
                  </TableCell>
                </TableRow>
              ) : (
                query.data.rows.map((row) => {
                  const delta = row.delta;
                  const hasDelta = typeof delta === 'number' && Number.isFinite(delta);
                  const isMismatch = hasDelta && delta !== 0;

                  return (
                    <TableRow key={row.skuId}>
                      <TableCell className="font-mono text-sm">{row.sku}</TableCell>
                      <TableCell className="max-w-[520px] truncate" title={row.title ?? row.sku}>
                        {row.title ?? '-'}
                        {!row.shopifyInventoryItemId && (
                          <span className="ml-2 text-xs text-muted-foreground">
                            {t('reconciliation.missingShopifyId')}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">{row.erpOnHand}</TableCell>
                      <TableCell className="text-right">
                        {row.shopifyOnHand === null ? '-' : row.shopifyOnHand}
                      </TableCell>
                      <TableCell className={isMismatch ? 'text-right font-medium text-destructive' : 'text-right'}>
                        {row.delta === null ? '-' : row.delta}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>

        {query.data && (
          <div className="flex items-center justify-between">
            <div className="text-sm text-muted-foreground">
              Showing {(page - 1) * pageSize + 1}-{Math.min(page * pageSize, query.data.total)} of {query.data.total}
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={!canPrev}
              >
                {t('reconciliation.prev')}
              </Button>
              <div className="text-sm text-muted-foreground">
                {t('table.page', { current: page, total: totalPages })}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => p + 1)}
                disabled={!canNext}
              >
                {t('reconciliation.next')}
              </Button>
            </div>
          </div>
        )}
        <BackToTopPill />
      </div>
    </div>
  );
}

export default function InventoryReconciliationWithErrorBoundary() {
  return (
    <PageErrorBoundary>
      <InventoryReconciliation />
    </PageErrorBoundary>
  );
}
